import {
  ref, computed, inject, unref,
} from '@vue/composition-api'
import useCurrentData from './useCurrentData'
import useSdpSlipPrint from './useSdpSlipPrint'
import { canExecutePrintOnThisDevice } from './useReceiptPrinterDeviceSetting'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { reportPrinterError } from '@/utils/sentryReportPrinterError'

/**
 * キャッシュドロワー(レシートプリンタのDKポート接続)を開く。
 *
 * 店舗の印刷経路に合わせて2系統を切り替える:
 * - SDP有効店舗: `drawer_kick` チケット(印字なし・パルスのみ)をサーバー経由で依頼する。
 *   ドロワーの開閉はその場で分かるため完了ポーリング・成功トーストは省略する(watch: false)
 * - それ以外: ブラウザ直結の ePOS SDK で addPulse を送る。
 *   SDP店舗では useReceiptPrinterConnection が printer を null のままにするので、直結送信は自動的に無効になる
 *
 * レイアウト(LayoutAdmin / LayoutWaiter / LayoutCustom)が provide する
 * `isReceiptPrinterConnected` / `printer` を inject するため、setup 内で呼ぶこと。
 *
 * 使い方:
 *   const { canKick, isKicking, kick } = useCashDrawerKick({ toast: vm.$toast })
 *   const { status } = await kick() // 'requested' | 'sent' | 'skipped' | 'failed' | 'request_failed'
 */
export default function useCashDrawerKick({ toast } = {}) {
  const { currentClub } = useCurrentData()
  const isReceiptPrinterConnected = inject('isReceiptPrinterConnected', false)
  const printer = inject('printer', null)
  const { isSdpEnabled, requestPrint } = useSdpSlipPrint({ toast })
  const isKicking = ref(false)

  // レシートプリンタ接続済み(SDP店舗は常にtrue扱い)のときだけ開ける
  const canKick = computed(() => !!unref(isReceiptPrinterConnected))

  const receiptPrinterContext = () => ({
    kind: 'receipt',
    ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
    clubId: currentClub.value?.id,
  })

  // ブラウザ直結経路。ドロワーはレシートプリンタのDKポートに接続されている前提で、パルス専用の独立ジョブを送る。
  // 応答は直前の伝票印刷が設定したonreceiveに流れるが、成功応答はno-opのため実害はない
  const kickDirect = () => {
    const eposPrinter = unref(printer)
    if (!eposPrinter) return false
    try {
      eposPrinter.addPulse(eposPrinter.DRAWER_1, eposPrinter.PULSE_100)
      eposPrinter.send()

      return true
    } catch (error) {
      console.error(error)
      reportPrinterError(error, { ...receiptPrinterContext(), phase: 'print', slipKind: 'drawer-kick' })

      return false
    }
  }

  // 店舗の印刷経路(SDP / 直結)に応じてドロワーを開く
  const kick = async () => {
    if (!canKick.value || !canExecutePrintOnThisDevice()) return { status: 'skipped' }

    isKicking.value = true
    try {
      if (isSdpEnabled()) {
        return await requestPrint({ ticketType: 'drawer_kick', watch: false })
      }

      return { status: kickDirect() ? 'sent' : 'failed' }
    } finally {
      isKicking.value = false
    }
  }

  return {
    canKick,
    isKicking,
    kickDirect,
    kick,
  }
}
